import React, { useState } from 'react'

import { useRouter } from 'next/router'
import { format, subDays, isBefore } from 'date-fns'
import dayjs from 'dayjs'
import TextField from '@mui/material/TextField'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { StaticDatePicker } from '@mui/x-date-pickers/StaticDatePicker'
import { MobileDatePicker } from '@mui/x-date-pickers/MobileDatePicker'
import { PickersDay } from '@mui/x-date-pickers/PickersDay'

import { useUserSettings } from 'contexts/usersettings'
import { useClientRect, getWindowSize } from 'hooks'
import { useHighestHazard } from 'clients/hazard'
import { START_DATE_OF_FLEXIBLE_FORECAST_REGIONS } from 'pages/forecasts/archive/[date]'
import css from './ArchiveWidget.module.css'

const MOBILE_CUTOFF = 768

export const ArchiveDatePicker = () => {
    const router = useRouter()
    const { query } = router
    const { date, lat, lng } = query
    const pinned = lat && lng
    const [selectedDate, setSelectedDate] = useState(dayjs(date))
    const [archiveDangerRatingQueryDate, setArchiveDangerRatingQueryDate] = useState(dayjs(date)) // Used to query the archive danger rating data
    const { colourblindModeEnabled } = useUserSettings()
    const minDate = dayjs(START_DATE_OF_FLEXIBLE_FORECAST_REGIONS)
    const maxDate = dayjs(new Date()) // Today
    const hazardResponse = useHighestHazard({
        lat,
        lng,
        fromDate: archiveDangerRatingQueryDate.startOf('month').subtract(1, 'month').toISOString(),
        toDate: archiveDangerRatingQueryDate.endOf('month').add(1, 'month').toISOString(),
    })
    const [rect, ref] = useClientRect(() => getWindowSize())

    const handleMonthChange = date => {
        setArchiveDangerRatingQueryDate(dayjs(date))
    }

    const updateSelectedDate = formattedDate => {
        setSelectedDate(dayjs(formattedDate))
        router.push(
            {
                query: {
                    ...query,
                    date: formattedDate,
                },
            },
            undefined,
            { shallow: true }
        )
    }

    const renderPickerDay = date => {
        const convertedDate = format(date.day['$d'], 'YYYY-MM-DD')
        const dateIteration = new Date(date.day['$d'])
        const disable = disableDay(dateIteration)
        const dangerData =
            pinned && hazardResponse.data
                ? hazardResponse.data.find(
                      element => format(element.date, 'YYYY-MM-DD') === convertedDate
                  )
                : undefined
        const dangerRating = dangerData ? dangerData.hazard.value : undefined
        const styles = colourblindModeEnabled
            ? colorMap[dangerRating + '--colourblind']
            : colorMap[dangerRating]
        const selectedStyles =
            convertedDate === format(selectedDate, 'YYYY-MM-DD')
                ? {
                      border: '3px solid #1565C0',
                  }
                : {}
        const slotStyles = {
            ...styles,
            ...selectedStyles,
            borderRadius: '3px',
            '&:hover': {
                opacity: dangerRating ? 0.8 : 1,
                backgroundColor: dangerRating ? styles?.backgroundColor : '', // HACK: `styles?.backgroundColor` is needed to prevent crashing for certain days, likely related to early-season danger ratings
            },
        }

        return (
            <PickersDay
                day={date.day['$d']}
                disabled={disable}
                onDaySelect={() => {
                    updateSelectedDate(format(date.day['$d'], 'YYYY-MM-DD'))
                }}
                sx={slotStyles}
            />
        )
    }

    const handleMobileDateChange = date => {
        updateSelectedDate(format(date, 'YYYY-MM-DD'))
    }

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs} ref={ref}>
            {rect.width < MOBILE_CUTOFF ? (
                // Note that mobile does not get the fancy coloured days in the datepicker
                <MobileDatePicker
                    value={selectedDate}
                    minDate={minDate}
                    maxDate={maxDate}
                    onChange={handleMobileDateChange}
                    className={css.MobileDatePicker}
                    renderInput={params => <TextField {...params} />}
                />
            ) : (
                <StaticDatePicker
                    displayStaticWrapperAs="desktop"
                    value={selectedDate}
                    minDate={minDate}
                    maxDate={maxDate}
                    onMonthChange={handleMonthChange}
                    slots={{
                        day: renderPickerDay,
                    }}
                    renderInput={params => <TextField {...params} />}
                />
            )}
        </LocalizationProvider>
    )
}

const disableDay = dateIteration => {
    const now = new Date()

    if (
        dateIteration.getTime() > now.getTime() ||
        isBefore(dateIteration, dayjs(START_DATE_OF_FLEXIBLE_FORECAST_REGIONS))
    ) {
        return true
    }

    return false
}

const colorMap = {
    low: {
        color: 'black',
        backgroundColor: '#50b848',
    },
    moderate: {
        color: 'black',
        backgroundColor: '#fff200',
    },
    considerable: {
        color: 'black',
        backgroundColor: '#f7941e',
    },
    high: {
        color: 'black',
        backgroundColor: '#ed1c24',
    },
    extreme: {
        color: 'white',
        backgroundColor: '#231f20',
    },
    norating: {
        color: 'black',
        backgroundColor: 'white',
    },
    'low--colourblind': {
        color: 'black',
        backgroundColor: '#fcffa4',
    },
    'moderate--colourblind': {
        color: 'black',
        backgroundColor: '#f98e09',
    },
    'considerable--colourblind': {
        color: 'white',
        backgroundColor: '#bc3754',
    },
    'high--colourblind': {
        color: 'white',
        backgroundColor: '#57106e',
    },
    'extreme--colourblind': {
        color: 'white',
        backgroundColor: '#000004',
    },
    'norating--colourblind': {
        color: 'black',
        backgroundColor: 'white',
    },
}
